// log-event.directive.ts
/*  Usage:
<!-- app.component.html -->
<div
  logEvent
  [initialDelay]="5"
  [intervalDelay]="10"
  [data]="logData"
  [maxSeconds]="120"
  mode="interval" 
  operation="projectinterest">
</div>

or

<div
  logEvent
  [initialDelay]="5"
  [data]="{projectId=...}"
  ( mode="single" )
  discriminator="Project|Asset"
  operation="access">
</div>
*/
import {
    Directive,
    Input,
    OnInit,
    OnDestroy,
    SimpleChanges
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, timer, interval } from 'rxjs';

@Directive({
  selector: '[logEvent]'
})
export class LogEventDirective implements OnInit, OnDestroy {
  @Input() initialDelay: number = 0;
  @Input() intervalDelay?: number;
  @Input() data: any = {};
  @Input() maxSeconds: number = 300;
  @Input() mode: 'single' | 'interval' = 'single';
  @Input() discriminator!: 'Project' | 'Asset';
  @Input() operation!: string;

  private intervalSub?: Subscription;
  private timeoutSub?: Subscription;
  private initialized = false;

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
  }

  // The logging operation can only start once the
  // necessary control parameters have been bound
  // from the caller
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized && this.discriminator && this.operation) {
      this.initialized = true;
      this.startLogging();
    }
  }

  private startLogging(): void {
    const operationCode = `${this.discriminator}${this.operation}`.toLowerCase();
    const logData = { ...this.data, initialDelay: this.initialDelay, elapsedTime: this.initialDelay };

    const sendDataPacketToLogger = () => {
      this.http.post('/api/LogEvent', {
        mode: this.mode,
        operation: operationCode,
        data: logData
      }).subscribe();
    };

    if (this.mode === 'interval') {
      const delay = this.intervalDelay ?? this.initialDelay;
      logData.intervalDelay = delay;
      logData.maxSeconds = this.maxSeconds;

      this.timeoutSub = timer(this.initialDelay * 1000)
        .subscribe(() => {
          sendDataPacketToLogger();

          let elapsedTime = this.initialDelay;
          this.intervalSub = interval(delay * 1000)
            .subscribe(() => {
              elapsedTime += delay;
              logData.elapsedTime = elapsedTime;

              if (elapsedTime >= this.maxSeconds && this.intervalSub) {
                this.intervalSub.unsubscribe();
              }

              sendDataPacketToLogger();
            });
        });
    } else {
      this.timeoutSub = timer(this.initialDelay * 1000).subscribe(() => {
        sendDataPacketToLogger();
      });
    }
  }


  ngOnDestroy(): void {
    this.timeoutSub?.unsubscribe();
    this.intervalSub?.unsubscribe();
  }
}
